// Reporting portal auth — Cognito user-pool calls made directly against the
// public Cognito Identity Provider API via fetch (no SDK). MFA is TOTP
// (authenticator app). We use raw fetch rather than amazon-cognito-identity-js
// (deprecated) or the AWS SDK v3 (see below).
//
// Why raw fetch instead of the AWS SDK v3: the reporting portal is bundled with
// a very old Parcel nightly that can't resolve the SDK's `node:` imports. The
// Cognito user-pool auth APIs (InitiateAuth / RespondToAuthChallenge / SignUp /
// etc.) are UNAUTHENTICATED public endpoints — they take the public ClientId
// and the user's password, never AWS credentials, and require NO SigV4 signing.
// So calling them with fetch is functionally identical to the SDK, over the
// same HTTPS endpoint, with no security downgrade — and one fewer dependency.
//
// AWS SDK v2 (global `AWS`) is still used ONLY for the Identity Pool credential
// exchange (CognitoIdentityCredentials) that vends temporary AWS creds for the
// signed database calls. The session is shaped like the old CognitoUserSession
// (getIdToken().getJwtToken()) so that path and extractGroupsFromToken work
// unchanged.

AWS.config.region = 'ap-southeast-2';

const REGION = 'ap-southeast-2';
const identityPoolId = 'ap-southeast-2:3a444625-0983-417d-bbbe-8d2a4b6d2e60';

// Cognito User Pool for reporting portal users (separate from admin)
const USER_POOL_ID = 'ap-southeast-2_98zn9vMbF';
const CLIENT_ID = '2601v4us0gmkt5902hcv5ioms8';

const IDP_ENDPOINT = `https://cognito-idp.${REGION}.amazonaws.com/`;

// localStorage key for the persisted Cognito refresh token. Used to re-establish
// the in-memory session on a page reload (restoreSession) since the session
// itself is never persisted. Never log the token value.
const REFRESH_TOKEN_KEY = 'reportingRefreshToken';
const AUTH_STATE_KEY = 'reportingAuth';

// ---------------------------------------------------------------------------
// Low-level call to the Cognito Identity Provider public API.
// `action` is e.g. "InitiateAuth"; `body` is the JSON request object.
// Cognito returns errors as JSON with __type + message; we throw an Error with
// a `.code` (the exception name) so callers can branch on it like before.
// ---------------------------------------------------------------------------
async function idpCall(action, body) {
  let resp;
  try {
    resp = await fetch(IDP_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-amz-json-1.1',
        'X-Amz-Target': `AWSCognitoIdentityProviderService.${action}`,
      },
      body: JSON.stringify(body),
    });
  } catch (networkErr) {
    const e = new Error('Network error contacting authentication service.');
    e.code = 'NetworkError';
    throw e;
  }

  const text = await resp.text();
  let data = {};
  if (text) {
    try { data = JSON.parse(text); } catch (_) { /* non-JSON */ }
  }

  if (!resp.ok) {
    // __type looks like "...#NotAuthorizedException"; take the trailing name.
    const type = (data.__type || '').split('#').pop() || `HTTP_${resp.status}`;
    const err = new Error(data.message || data.Message || type);
    err.code = type;
    err.name = type;
    throw err;
  }

  return data;
}

// ---------------------------------------------------------------------------
// Session shim — wraps the raw AuthenticationResult (plain JWT strings) in the
// CognitoUserSession-like shape the rest of the app expects.
// ---------------------------------------------------------------------------
function makeSession(authResult) {
  const idToken = authResult.IdToken;
  const accessToken = authResult.AccessToken;
  const refreshToken = authResult.RefreshToken;
  return {
    getIdToken: () => ({ getJwtToken: () => idToken }),
    getAccessToken: () => ({ getJwtToken: () => accessToken }),
    getRefreshToken: () => ({ getToken: () => refreshToken }),
    idToken: { jwtToken: idToken },
  };
}

class AuthManager {
  constructor() {
    this.callbacks = [];
    this.credentials = null;
    this.isGuest = false;
    this.refreshPromise = null;
    this.session = null;
  }

  onAuthChange(callback) {
    this.callbacks.push(callback);
  }

  notify(isAuthenticated) {
    this.callbacks.forEach(cb => cb(isAuthenticated));
  }

  async getCredentials() {
    if (this.refreshPromise) {
      await this.refreshPromise;
      return this.credentials;
    }

    if (!this.credentials || this.credentials.needsRefresh()) {
      this.refreshPromise = this._refreshCredentials();
      await this.refreshPromise;
      this.refreshPromise = null;
    }
    return this.credentials;
  }

  async _refreshCredentials() {
    // If we have a session (user logged in via MFA), use authenticated flow
    if (this.session) {
      const token = this.session.getIdToken().getJwtToken();
      this.credentials = new AWS.CognitoIdentityCredentials({
        IdentityPoolId: identityPoolId,
        Logins: {
          [`cognito-idp.ap-southeast-2.amazonaws.com/${USER_POOL_ID}`]: token
        }
      });
    } else {
      // Unauthenticated flow — restricted access (themes + client group validation only)
      this.credentials = new AWS.CognitoIdentityCredentials({
        IdentityPoolId: identityPoolId
      });
    }

    await new Promise((resolve, reject) => {
      this.credentials.refresh(err => {
        if (err) reject(err);
        else {
          this.isGuest = !this.session;
          resolve();
        }
      });
    });
  }

  setSession(session) {
    this.session = session;
    // Clear cached credentials so next getCredentials() uses the authenticated flow
    this.credentials = null;
  }

  clearSession() {
    this.session = null;
    this.credentials = null;
    this.isGuest = false;
    // Drop the persisted refresh token so clearSession() fully de-auths.
    try { localStorage.removeItem(REFRESH_TOKEN_KEY); } catch (_) { /* no storage */ }
  }

  async isAuthenticated() {
    try {
      await this.getCredentials();
      return true;
    } catch (err) {
      return false;
    }
  }

  getCurrentUser() {
    return this.isGuest ? { id: 'guest' } : null;
  }
}

const auth = new AuthManager();

// Guest/Identity Pool exports (for initial theme loading)
export const getCurrentUser = () => auth.getCurrentUser();
export const getCredentials = () => auth.getCredentials();
export const isAuthenticated = () => auth.isAuthenticated();
export const onAuthChange = (callback) => auth.onAuthChange(callback);
export const clearSession = () => auth.clearSession();
// True once a Cognito ID token exists (database.js only sends X-Id-Token then).
export const hasIdToken = () => {
  try { return !!auth.session?.getIdToken?.().getJwtToken?.(); } catch (_) { return false; }
};

// ---------------------------------------------------------------------------
// Restore the in-memory Cognito session on app load from a persisted refresh
// token. The session (ID token holder) lives only in memory, so after a reload
// a rehydrated "logged in" UI has no session and data calls fail. This exchanges
// the stored refresh token via REFRESH_TOKEN_AUTH for a fresh ID/access token
// and repopulates auth.session.
//
// Returns true when the session is re-established, false otherwise (no stored
// token, or the refresh token is expired/revoked). On an auth failure the
// persisted auth state is cleared so the UI falls back to the login page rather
// than looping. Never throws; never logs the token value.
// ---------------------------------------------------------------------------
export async function restoreSession() {
  let rt;
  try { rt = localStorage.getItem(REFRESH_TOKEN_KEY); } catch (_) { rt = null; }
  if (!rt) return false;

  let resp;
  try {
    resp = await idpCall('InitiateAuth', {
      AuthFlow: 'REFRESH_TOKEN_AUTH',
      ClientId: CLIENT_ID,
      AuthParameters: { REFRESH_TOKEN: rt },
    });
  } catch (err) {
    // NotAuthorizedException = expired/revoked refresh token. Any failure means
    // we cannot restore — clear persisted auth so the UI shows the login page.
    try { localStorage.removeItem(REFRESH_TOKEN_KEY); } catch (_) { /* no storage */ }
    try { localStorage.removeItem(AUTH_STATE_KEY); } catch (_) { /* no storage */ }
    return false;
  }

  if (!resp || !resp.AuthenticationResult) {
    try { localStorage.removeItem(REFRESH_TOKEN_KEY); } catch (_) { /* no storage */ }
    try { localStorage.removeItem(AUTH_STATE_KEY); } catch (_) { /* no storage */ }
    return false;
  }

  // REFRESH_TOKEN_AUTH returns no RefreshToken — keep reusing the stored one.
  auth.setSession(makeSession(resp.AuthenticationResult));
  return true;
}

// ---------------------------------------------------------------------------
// Sign-in — password primary factor, TOTP authenticator app as MFA.
//
// Resolves with a session on success. Rejects with a typed object the UI state
// machine keys off:
//   NEW_PASSWORD_REQUIRED  — force-change-password challenge
//   MFA_SETUP              — first login; user must enrol their authenticator
//   MFA_REQUIRED           — enter the current code from the authenticator app
// The rejected object carries { username, session } to answer the challenge.
// ---------------------------------------------------------------------------
export const signIn = async (username, password) => {
  const resp = await idpCall('InitiateAuth', {
    AuthFlow: 'USER_PASSWORD_AUTH',
    ClientId: CLIENT_ID,
    AuthParameters: { USERNAME: username, PASSWORD: password },
  });
  return handleAuthResponse(resp, username);
};

// Interprets an InitiateAuth / RespondToAuthChallenge response: either a
// finished session, or a challenge we surface to the UI.
function handleAuthResponse(resp, username) {
  if (resp.AuthenticationResult) {
    const session = makeSession(resp.AuthenticationResult);
    auth.setSession(session);
    // Persist the refresh token so the session can be rebuilt on reload.
    // A REFRESH_TOKEN_AUTH response carries no RefreshToken — leave the stored
    // one intact in that case (do not clear it). Never log the token value.
    const refreshToken = resp.AuthenticationResult.RefreshToken;
    if (typeof refreshToken === 'string' && refreshToken) {
      try { localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken); } catch (_) { /* no storage */ }
    }
    return session;
  }

  const challenge = resp.ChallengeName;
  const session = resp.Session;

  if (challenge === 'NEW_PASSWORD_REQUIRED') {
    return Promise.reject({ type: 'NEW_PASSWORD_REQUIRED', username, session });
  }

  // First-time TOTP enrolment — user must scan a QR and register their app.
  if (challenge === 'MFA_SETUP') {
    return Promise.reject({ type: 'MFA_SETUP', username, session });
  }

  // MFA challenge — user enters the current code from their authenticator app
  // (or, if ever configured, an emailed/SMS code).
  if (challenge === 'SOFTWARE_TOKEN_MFA' || challenge === 'EMAIL_OTP' || challenge === 'SMS_MFA' || challenge === 'SMS_OTP') {
    return Promise.reject({ type: 'MFA_REQUIRED', mfaChallenge: challenge, username, session });
  }

  // Multiple MFA types configured — Cognito asks us to pick.
  if (challenge === 'SELECT_MFA_TYPE') {
    return Promise.reject({ type: 'SELECT_MFA_TYPE', username, session });
  }

  return Promise.reject({ type: 'UNKNOWN_CHALLENGE', challenge, username, session });
}

// ---------------------------------------------------------------------------
// Verify the MFA code at login. `challengeState` is the object rejected by
// signIn (contains { username, session } and optionally mfaChallenge).
// Defaults to TOTP (authenticator app).
// ---------------------------------------------------------------------------
export const verifyMFA = async (challengeState, code) => {
  const username = challengeState.username;
  const challengeName = challengeState.mfaChallenge || 'SOFTWARE_TOKEN_MFA';

  const codeKey = {
    SOFTWARE_TOKEN_MFA: 'SOFTWARE_TOKEN_MFA_CODE',
    EMAIL_OTP: 'EMAIL_OTP_CODE',
    SMS_OTP: 'SMS_OTP_CODE',
    SMS_MFA: 'SMS_MFA_CODE',
  }[challengeName] || 'SOFTWARE_TOKEN_MFA_CODE';

  const resp = await idpCall('RespondToAuthChallenge', {
    ClientId: CLIENT_ID,
    ChallengeName: challengeName,
    Session: challengeState.session,
    ChallengeResponses: {
      USERNAME: username,
      [codeKey]: code,
    },
  });

  return handleAuthResponse(resp, username);
};

// ---------------------------------------------------------------------------
// TOTP enrolment (first-time MFA setup). Two steps:
//   1. setupMFA(challengeState)  -> AssociateSoftwareToken; returns the secret
//      to render as a QR code, plus a fresh session to carry forward.
//   2. verifyMFASetup(...)       -> VerifySoftwareToken with the user's first
//      code, then RespondToAuthChallenge(MFA_SETUP) to complete the login and
//      obtain tokens.
// ---------------------------------------------------------------------------
export const setupMFA = async (challengeState) => {
  const resp = await idpCall('AssociateSoftwareToken', {
    Session: challengeState.session,
  });
  return {
    secretCode: resp.SecretCode,
    // AssociateSoftwareToken returns a new session to use for VerifySoftwareToken.
    session: resp.Session || challengeState.session,
    username: challengeState.username,
  };
};

export const verifyMFASetup = async (setupState, code) => {
  // Step 1: verify the user's authenticator produces the right code.
  const verifyResp = await idpCall('VerifySoftwareToken', {
    Session: setupState.session,
    UserCode: code,
    FriendlyDeviceName: 'Reporting Portal',
  });

  if (verifyResp.Status !== 'SUCCESS') {
    const err = new Error('Could not verify the authenticator code.');
    err.code = 'EnableSoftwareTokenMFAException';
    throw err;
  }

  // Step 2: complete the sign-in by answering the MFA_SETUP challenge with the
  // (now verified) session, which yields the authentication tokens.
  const resp = await idpCall('RespondToAuthChallenge', {
    ClientId: CLIENT_ID,
    ChallengeName: 'MFA_SETUP',
    Session: verifyResp.Session,
    ChallengeResponses: {
      USERNAME: setupState.username,
    },
  });

  return handleAuthResponse(resp, setupState.username);
};

// ---------------------------------------------------------------------------
// Sign-up / confirmation
// ---------------------------------------------------------------------------
export const signUp = async (email, password) => {
  const resp = await idpCall('SignUp', {
    ClientId: CLIENT_ID,
    Username: email,
    Password: password,
    UserAttributes: [{ Name: 'email', Value: email }],
  });
  return { username: email, userSub: resp.UserSub, userConfirmed: resp.UserConfirmed };
};

export const confirmSignUp = async (email, code) => {
  return idpCall('ConfirmSignUp', {
    ClientId: CLIENT_ID,
    Username: email,
    ConfirmationCode: code,
  });
};

export const resendConfirmationCode = async (email) => {
  return idpCall('ResendConfirmationCode', {
    ClientId: CLIENT_ID,
    Username: email,
  });
};

// ---------------------------------------------------------------------------
// Force-change-password (NEW_PASSWORD_REQUIRED). `challengeState` is the object
// rejected by signIn. May itself lead to an MFA challenge.
// ---------------------------------------------------------------------------
export const completeNewPassword = async (challengeState, newPassword) => {
  const username = challengeState.username;
  const resp = await idpCall('RespondToAuthChallenge', {
    ClientId: CLIENT_ID,
    ChallengeName: 'NEW_PASSWORD_REQUIRED',
    Session: challengeState.session,
    ChallengeResponses: {
      USERNAME: username,
      NEW_PASSWORD: newPassword,
    },
  });
  return handleAuthResponse(resp, username);
};

// ---------------------------------------------------------------------------
// Forgot / reset password
// ---------------------------------------------------------------------------
export const forgotPassword = async (email) => {
  return idpCall('ForgotPassword', {
    ClientId: CLIENT_ID,
    Username: email,
  });
};

export const confirmPassword = async (email, code, newPassword) => {
  return idpCall('ConfirmForgotPassword', {
    ClientId: CLIENT_ID,
    Username: email,
    ConfirmationCode: code,
    Password: newPassword,
  });
};

// ---------------------------------------------------------------------------
// Token helpers
// ---------------------------------------------------------------------------
export const extractGroupsFromToken = (session) => {
  try {
    let token;
    if (session && typeof session.getIdToken === 'function') {
      token = session.getIdToken().getJwtToken();
    } else if (session && session.idToken) {
      token = session.idToken.jwtToken;
    } else {
      console.warn('extractGroupsFromToken: unexpected session format', session);
      return [];
    }
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload['cognito:groups'] || [];
  } catch (e) {
    console.warn('Failed to extract groups from token:', e);
    return [];
  }
};

export default auth;
